import React, { useEffect, useState } from "react";
import {
  Card,
  Space,
  Row,
  Col,
  notification,
  Table,
  TableColumnsType,
  Typography,
  Button,
  message,
  Spin,
  Tag,
  Progress,
  Tooltip,
} from "antd";
import { useParams, Link, useNavigate } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { usePermissions } from "../../../PermissionsProvider";
import moment from "moment";
import { CopyOutlined, EyeOutlined, EditOutlined, PlusOutlined, ToolOutlined } from "@ant-design/icons";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import RequestView from "../../shared/RequestView";
import RequestViewModal from "../../Request/RequestViewModal";
import { ReactComponent as AiStars } from "../../../assets/svg/ai-creation.svg";
import DatasetModification from "./DatasetModification";

const { Text, Title } = Typography;

const DatasetDetail: React.FC = () => {
  const { id } = useParams();
  const { apiKey } = usePermissions();
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [requests, setRequests] = useState<ChatCompletionRequest[]>([]);
  const [finetuneRuns, setFinetuneRuns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [isRunsLoading, setIsRunsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(5);
  const [runsCurrentPage, setRunsCurrentPage] = useState<number>(1);
  const [runsPageSize, setRunsPageSize] = useState<number>(5);
  const [totalRuns, setTotalRuns] = useState<number>(0);
  const [runsByPage, setRunsByPage] = useState<Record<number, any[]>>({});
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, ChatCompletionRequest[]>
  >({});
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [isModificationModalVisible, setIsModificationModalVisible] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ChatCompletionRequest | null>(null);
  const navigate = useNavigate();

  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  const updateDatasetRequest = (request: ChatCompletionRequest) => {
    setRequests((prev) =>
      prev.map((r) => (r.request_id === request.request_id ? request : r))
    );
  };

  // Fetch dataset details
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      console.log("Fetching dataset with ID:", id);
      DatasetService.getDataset(Number(id), setIsLoading, apiKey).subscribe(
        (data: FinetuneDataset) => {
          console.log("Dataset data received:", data);
          if (data) {
            console.log("Dataset properties:", Object.keys(data));
            setDataset(data);
          } else {
            console.error("Received empty dataset data");
            notification.error({
              message: "Error",
              description: "Failed to fetch dataset: Empty response received",
            });
          }
        },
        (error: any) => {
          console.error("Error fetching dataset:", error);
          notification.error({
            message: "Error",
            description: `Failed to fetch dataset: ${error}`,
          });
        }
      );
    }
  }, [id, apiKey]);

  // Fetch finetune runs for the dataset
  useEffect(() => {
    if (id && !runsByPage[runsCurrentPage]) {
      fetchFinetuneRuns(runsCurrentPage);
    } else if (runsByPage[runsCurrentPage]) {
      setFinetuneRuns(runsByPage[runsCurrentPage]);
    }
  }, [id, apiKey, runsCurrentPage, runsPageSize, runsByPage]);

  const fetchFinetuneRuns = (page: number) => {
    if (!id) return;

    setIsRunsLoading(true);
    const offset = (page - 1) * runsPageSize;
    console.log(
      `Fetching finetune runs for dataset ${id}, page ${page}, offset ${offset}, limit ${runsPageSize}`
    );

    FinetuneRunService.getFinetuneRunsByDatasetId(
      Number(id),
      offset,
      runsPageSize,
      setIsRunsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        console.log("Finetune runs received:", response);

        setFinetuneRuns(response.items || []);
        setTotalRuns(response.total);
        setRunsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
      },
      (error: any) => {
        console.error("Error fetching finetune runs:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch finetune runs: ${error}`,
        });
      }
    );
  };

  // Fetch dataset requests
  useEffect(() => {
    if (id && !requestsByPage[currentPage]) {
      fetchRequests(currentPage);
    } else if (requestsByPage[currentPage]) {
      setRequests(requestsByPage[currentPage]);
    }
  }, [id, currentPage, requestPageSize, requestsByPage]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * requestPageSize;
    setIsRequestsLoading(true);
    console.log(
      `Fetching requests for dataset ${id}, page ${page}, offset ${offset}, limit ${requestPageSize}`
    );

    DatasetService.getDatasetRequests(
      Number(id),
      offset,
      requestPageSize,
      setIsRequestsLoading,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        console.log("Dataset requests received:", response);
        setRequests(response.items);
        setTotalRequests(response.total);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching dataset requests:", error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  const getStatusTagColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case "CREATED":
        return "default";
      case "SCHEDULED":
        return "processing";
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      case "RUNNING":
        return "processing";
      case "PENDING":
        return "warning";
      default:
        return "default";
    }
  };

  const requestColumns: TableColumnsType<ChatCompletionRequest> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      key: "tags",
      width: "45%",
      render: (_, request: ChatCompletionRequest) => {
        try {
          const metaData = JSON.parse(
            (request.metadata?._maitai_request_meta as string) || "{}"
          );
          const tags = metaData.tags || [];
          return (
            <Space wrap>
              {tags.map((tag: string) => (
                <Tag key={tag} bordered={true}>
                  {tag}
                </Tag>
              ))}
            </Space>
          );
        } catch (e) {
          return null;
        }
      },
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "20%",
      render: (_, request: ChatCompletionRequest) => {
        const requestId = request.request_id || "";
        const displayId =
          requestId.length > 8
            ? `...${requestId.substring(requestId.length - 8)}`
            : requestId;

        return (
          <Space style={{ width: "100%" }}>
            {displayId}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_, record: ChatCompletionRequest) => (
        <Tooltip
          title={
            <RequestView
              request={record}
              apiKey={apiKey}
              isEditable={false}
              onUpdated={(request: ChatCompletionRequest) => {
                updateDatasetRequest(request);
              }}
            />
          }
          placement="left"
          overlayStyle={{ maxWidth: "600px", width: "600px" }}>
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => handleViewRequest(record)}>
            View
          </Button>
        </Tooltip>
      ),
    },
  ];

  // Updated finetuning runs columns to match FinetuneRuns.tsx
  const finetuningRunsColumns: TableColumnsType<any> = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => {
        return record.display_name;
      },
      sorter: (a, b) => {
        const aName = a.display_name as string;
        const bName = b.display_name as string;
        return aName.localeCompare(bName);
      },
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) => moment(date).format("YYYY-MM-DD hh:mm:ss A"),
      sorter: (a, b) =>
        moment(a.date_created).unix() - moment(b.date_created).unix(),
    },
    {
      title: "Data Source",
      key: "data_source",
      render: (_, record) => {
        if (record.intent_group_name) {
          return record.intent_group_name;
        } else if (record.application_name && record.intent_name) {
          return `${record.application_name} / ${record.intent_name}`;
        } else {
          return "";
        }
      },
    },
    {
      title: "Base Model",
      dataIndex: "base_model",
      key: "base_model",
      sorter: (a, b) => a.base_model.localeCompare(b.base_model),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const statusUpper = status.toUpperCase();

        if (statusUpper === "RUNNING") {
          const percent = record.progress
            ? Math.round(record.progress * 100)
            : 0;
          return (
            <div style={{ width: "100px" }}>
              <Progress
                percent={Number(percent.toFixed(1))}
                size="small"
                status="active"
              />
            </div>
          );
        } else if (statusUpper === "COMPLETED") {
          return (
            <div style={{ width: "100px" }}>
              <Progress percent={100} size="small" />
            </div>
          );
        } else {
          return <Tag color={getStatusTagColor(status)}>{statusUpper}</Tag>;
        }
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}>
          <Col flex="auto">
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              {!dataset ? (
                <Spin size="small" />
              ) : (
                <>
                  <Link to={"/models/datasets"} style={{ color: "#828282" }}>
                    Datasets
                    {" / "}{" "}
                  </Link>
                  {dataset.name}
                </>
              )}
            </Typography>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/models/datasets/${id}/edit`)}>
              Edit Dataset
            </Button>
          </Col>
        </Row>

        {/* Top Details Section - Updated to match TestSetDetail.tsx style */}
        {dataset && (
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: "16px" }}>
                Details
              </Text>
            }
            style={{
              background: "#f8f9fa",
              borderRadius: "8px",
              marginBottom: "12px",
            }}
            loading={isLoading}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary">Description</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {dataset.description || "No description provided"}
                  </Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">Date Created</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {moment(dataset.date_created).format(
                      "MMM D, YYYY HH:mm:ss"
                    )}
                  </Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">Total Requests</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {dataset.request_count || 0}
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Source</Text>
                <div>
                  <Text strong style={{ fontSize: "14px" }}>
                    {dataset.intent_group_name &&
                    !dataset.application_id &&
                    !dataset.intent_id ? (
                      <Link to={`/intent-groups/${dataset.intent_group_id}`}>
                        {dataset.intent_group_name}
                      </Link>
                    ) : dataset.application_name &&
                      dataset.intent_name &&
                      !dataset.intent_group_id ? (
                      <>
                        <Link to={`/application/${dataset.application_id}`}>
                          {dataset.application_name}
                        </Link>
                        {" / "}
                        <Link
                          to={`/application/${dataset.application_id}/intent/${dataset.intent_id}`}>
                          {dataset.intent_name}
                        </Link>
                      </>
                    ) : (
                      "Unknown source"
                    )}
                  </Text>
                </div>
              </Col>
              {dataset.intent_group_id && (
                <Col span={6}>
                  <Text type="secondary">Intent Group ID</Text>
                  <div>
                    <Text>{dataset.intent_group_id}</Text>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        )}

        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4}>
              Finetuning Runs
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate(`/models/finetuning/new?dataset_id=${id}`)
              }>
              New Finetuning Run
            </Button>
          </Col>
        </Row>

        {isRunsLoading ? (
          <Spin
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          />
        ) : (
          <Table
            rowKey={(record) => record.id.toString()}
            columns={finetuningRunsColumns}
            dataSource={finetuneRuns}
            pagination={{
              current: runsCurrentPage,
              pageSize: runsPageSize,
              total: totalRuns,
              onChange: (page, pageSize) => {
                setRunsCurrentPage(page);
                if (pageSize && pageSize !== runsPageSize) {
                  setRunsPageSize(pageSize);
                  setRunsByPage({});
                }
              },
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50"],
            }}
            onRow={(record) => {
              return {
                onClick: () => navigate(`/models/finetuning/${record.id}`),
              };
            }}
            locale={{ emptyText: "No finetuning runs yet" }}
          />
        )}

        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4}>
              Requests
            </Title>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<ToolOutlined />}
              onClick={() => setIsModificationModalVisible(true)}>
              Broad Modification
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={requests}
          columns={requestColumns}
          rowKey="request_id"
          loading={isRequestsLoading}
          pagination={{
            current: currentPage,
            pageSize: requestPageSize,
            total: totalRequests,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              if (pageSize && pageSize !== requestPageSize) {
                setRequestPageSize(pageSize);
                setRequestsByPage({});
              }
            },
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setRequestPageSize(size);
              setRequestsByPage({});
            },
          }}
        />
        
        {/* Dataset Modification Modal */}
        {dataset && (
          <DatasetModification
            datasetId={Number(id)}
            isVisible={isModificationModalVisible}
            onClose={() => setIsModificationModalVisible(false)}
          />
        )}
        
        {/* Add RequestViewModal */}
        {selectedRequest && (
          <RequestViewModal
            request={selectedRequest}
            isVisible={isModalVisible}
            onClose={handleCloseModal}
            apiKey={apiKey}
          />
        )}
      </Space>
    </Card>
  );
};

export default DatasetDetail;
